import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  DollarSign, Download, Filter, Calendar, ArrowUpRight,
  ArrowDownRight, FileText, Clock, Building2, Users,
  CreditCard, Wallet, Receipt, PieChart, BarChart2,
  AlertTriangle, CheckCircle2, Plus, Heart, Award, MinusCircle
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { payrollData, employeeData } from '../data/mockData';

const PayrollSummaryCard = ({ title, amount, icon: Icon, trend, color }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    className="bg-white rounded-xl p-6 shadow-sm"
  >
    <div className="flex justify-between items-start mb-4">
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <h3 className="text-2xl font-bold text-gray-800 mt-1">
          SAR {amount.toLocaleString()}
        </h3>
        {trend && (
          <p className={`text-sm mt-1 flex items-center gap-1
            ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}
          >
            {trend > 0 ? (
              <ArrowUpRight className="w-4 h-4" />
            ) : (
              <ArrowDownRight className="w-4 h-4" />
            )}
            {Math.abs(trend)}% vs last month
          </p>
        )}
      </div>
      <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </motion.div>
);

const PaymentHistoryCard = ({ payment }) => (
  <div className="flex items-center justify-between p-4 bg-white rounded-xl hover:bg-gray-50 transition-colors">
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
        <span className="text-lg font-semibold text-indigo-600">
          {payment.employee.split(' ').map(n => n[0]).join('')}
        </span>
      </div>
      <div>
        <h3 className="font-semibold text-gray-900">{payment.employee}</h3>
        <p className="text-sm text-gray-500">{payment.type}</p>
      </div>
    </div>
    <div className="text-right">
      <p className="font-semibold text-gray-900">SAR {payment.amount.toLocaleString()}</p>
      <p className="text-sm text-gray-500">{payment.date}</p>
    </div>
  </div>
);

const DepartmentBudgetCard = ({ department }) => (
  <div className="bg-white rounded-xl p-6 shadow-sm">
    <div className="flex justify-between items-start mb-4">
      <div>
        <h3 className="font-semibold text-gray-900">{department.name}</h3>
        <p className="text-sm text-gray-500">{department.employees} employees</p>
      </div>
      <span className="text-lg font-semibold text-gray-900">
        SAR {department.amount.toLocaleString()}
      </span>
    </div>
    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
      <div 
        className="h-full bg-indigo-600"
        style={{ width: `${(department.amount / payrollData.monthlySummary.totalSalaries) * 100}%` }}
      />
    </div>
  </div>
);

const PayrollModule = ({ isMobile }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [payrollStatus, setPayrollStatus] = useState('pending'); // pending, processing, completed

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex items-center gap-4">
          <input
            type="month"
            value={selectedMonth.toISOString().slice(0, 7)}
            onChange={(e) => setSelectedMonth(new Date(e.target.value))}
            className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
          />
          <select
            className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Departments</option>
            {employeeData.departments.map((dept, index) => (
              <option key={index} value={dept.name}>{dept.name}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <button className="px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 
            flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export Report
          </button>
          <button className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 
            flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Process Payroll
          </button>
        </div>
      </div>

      {/* Payroll Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <PayrollSummaryCard
          title="Total Salaries"
          amount={payrollData.monthlySummary.totalSalaries}
          icon={DollarSign}
          trend={2.5}
          color="bg-indigo-600"
        />
        <PayrollSummaryCard
          title="Benefits"
          amount={payrollData.monthlySummary.benefits}
          icon={Heart}
          color="bg-green-600"
        />
        <PayrollSummaryCard
          title="Bonuses"
          amount={payrollData.monthlySummary.bonuses}
          icon={Award}
          color="bg-amber-600"
        />
        <PayrollSummaryCard
          title="Deductions"
          amount={payrollData.monthlySummary.deductions}
          icon={MinusCircle}
          color="bg-red-600"
        />
      </div>

      {/* Department Budgets & Payment History */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Department Budgets */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Department Budgets</h3>
          {payrollData.departments.map((department, index) => (
            <DepartmentBudgetCard key={index} department={department} />
          ))}
        </div>

        {/* Recent Payments */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Payments</h3>
          <div className="space-y-4">
            {payrollData.recentPayments.map((payment) => (
              <PaymentHistoryCard key={payment.id} payment={payment} />
            ))}
          </div>
        </div>
      </div>

      {/* Payroll Analytics */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Payroll Analytics</h3>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={payrollData.departments}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="amount" fill="#4F46E5" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default PayrollModule; 